import { MB, useMount } from "constants/constants";
import { useAuth0 } from "contexts/AuthManager/Auth";
import { useEffect, useRef } from "react";
import { getBrowserInfo } from "utils/cssUtils";
import {
  useVisibleGroups,
  useVisibleProcesses,
} from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";

// context & diagrams: https://medium.com/@JackPu/how-to-check-browser-crash-via-javascript-fa7d5af5e80b

// modified from https://medium.com/@JackPu/how-to-check-browser-crash-via-javascript-fa7d5af5e80b
const HEARTBEAT_INTERVAL = 10 * 1000;

/** Send a heartbeat signal to the service worker script in public/service-worker-addon-crash-reports.js
 *
 * If service-worker-addon-crash-reports.js stops hearing the heartbeat, it reports a crash to Honeycomb.
 *
 * scenarios:
 * - user switches apps or tabs away = no crash report
 * - app crashes while in focus = crash report
 */
const useSWHeartBeat = () => {
  const timerRef = useRef(null as number | null);
  // service-worker sessionId: random 16-digit integer e.g. multiple tabs open, each has a session id
  const sessionId = useRef(Math.round(Math.random() * 10 ** 16));

  // data to send to Honeycomb
  const perf = window.performance as any; // gives us memory usage data (chrome only)
  const ckey = useCustomerKey();
  const { user } = useAuth0();
  const { forceTrace } = useFlagsWithDefaults();
  const visibleProcesses = useVisibleProcesses();
  const visibleGroups = useVisibleGroups();

  // * fix for service worker bug after hard-refreshes
  // if we just hard-refreshed, do a soft-refresh to reboot the service worker
  // https://stackoverflow.com/a/62596701/11718078
  useFixAfterHardRefresh();

  // start the heartbeat if the service worker is available
  // (could be immediately, or sometimes we must reboot the service worker
  // e.g. in the useMount above, after "Empty Cache and Hard Reload")
  useMount(() => {
    if (navigator?.serviceWorker?.controller) {
      window.addEventListener("beforeunload", () =>
        stopHeartbeatPeacefully({ sessionId, timerRef })
      );
      // start the interval to send heartbeats,
      startHeartbeat({ timerRef, sessionId });
    }
    return () => {
      // clear the timer when we unmount
      // (probably not necessary since it should only unmount when we close the app, just good hygiene)
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  });

  // handle focus/blur:
  // when the browser tab loses focus (e.g. when we switch tabs, or on mobile when we close the browser or switch apps),
  // timers like setInterval get deprioritized and become unstable, or stops entirely e.g. switch apps on mobile
  // so the heartbeat becomes unreliable;
  // -> so, only send crash reports if the tab is currently in focus
  useStopHeartbeatOnBlurStartOnFocus(timerRef, sessionId);

  // TODO: test on mobile - detect "switching away" as same as closing?

  // keep crash-report data updated
  useEffect(() => {
    const browserInfo = getBrowserInfo();

    try {
      window.localStorage.setItem(
        "data_SWHeartBeat",
        JSON.stringify({
          customer: String(ckey),
          email: String(user?.email),
          url: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`,
          "memory.totalJSHeapSizeMB": perf?.memory?.totalJSHeapSize / MB,
          "memory.usedJSHeapSizeMB": perf?.memory?.usedJSHeapSize / MB,
          "memory.jsHeapSizeLimitMB": perf?.memory?.jsHeapSizeLimit / MB,
          "honeycomb.dataset": "Flight",
          name: "Flight Crash Reports",
          flightVersion: process.env.REACT_APP_VERSION,
          flightHasCrashed: true,
          isForceTraceEnabled: forceTrace,
          groupName:
            visibleGroups.length === 1 ? visibleGroups[0].longName : "",
          processName:
            visibleProcesses.length === 1 ? visibleProcesses[0].name : "",
          serviceName: "flight",
          operatingSystem: browserInfo.os.name,
          browser: browserInfo.browser.name,
          browserVersion: browserInfo.browser.version,
          referrer: `${window.location.origin}${window.location.pathname}`, // used in the SW's fetch request
        })
      );
    } catch (error) {
      console.error("SWHeartBeat error:", error);
    }
  }, [ckey, perf, forceTrace, user, visibleGroups, visibleProcesses]);
};

export default useSWHeartBeat;

function useStopHeartbeatOnBlurStartOnFocus(
  timerRef: { current: number | null },
  sessionId: { current: number | null }
) {
  useEffect(() => {
    // User has switched back to the tab
    window.addEventListener("focus", () =>
      startHeartbeat({ timerRef, sessionId })
    );
    // User has switched away from the tab (AKA tab is hidden)
    // -> stop the heartbeat, don't report crash on background tabs
    window.addEventListener("blur", () =>
      stopHeartbeatPeacefully({ sessionId, timerRef })
    );
    return () => {
      window.removeEventListener("focus", () =>
        startHeartbeat({ timerRef, sessionId })
      );
      window.removeEventListener("blur", () =>
        stopHeartbeatPeacefully({ sessionId, timerRef })
      );
    };
  }, [timerRef, sessionId]);
}

function useFixAfterHardRefresh() {
  useMount(() => {
    navigator?.serviceWorker?.getRegistration()?.then(function (
      reg: ServiceWorkerRegistration | undefined
    ) {
      // There's an active SW, but no controller for this tab.
      if (reg?.active && !navigator?.serviceWorker?.controller) {
        // Perform a soft reload to load everything from the SW and get
        // a consistent set of resources.
        console.log("🤖 service worker needs to reboot, hang tight...");
        window.location.reload();
      }
    });
  });
}

/** send a "start" signal to the service worker,
 *
 * and stop tracking other background tabs (this part might be unnecessary, but just in case)
 */
function startHeartbeat({
  timerRef,
  sessionId,
}: {
  timerRef: { current: number | null };
  sessionId: { current: number | null };
}) {
  // clear all pages before we start - this will delete / stop tracking any background tabs that we switched away from
  navigator?.serviceWorker?.controller?.postMessage({
    type: "clearAllPages",
  });

  // send the first heartbeat immediately
  sendHeartbeat({ sessionId });
  // and start the interval
  timerRef.current = window.setInterval(
    () => sendHeartbeat({ sessionId }),
    HEARTBEAT_INTERVAL
  );
}

/** send a heartbeat with data to the service worker */
function sendHeartbeat({
  sessionId,
}: {
  sessionId: { current: number | null };
}) {
  const dataString = window.localStorage.getItem("data_SWHeartBeat");
  navigator?.serviceWorker?.controller?.postMessage({
    type: "heartbeat",
    id: sessionId.current,
    // this data will be attached to the crash report
    data: dataString ? JSON.parse(dataString) : "",
  });
}

/** send an "unload" signal to the service worker and stop sending the heartbeat
 *
 * e.g. if user has two tabs open, and they switch to one,
 * we stop listening to the heartbeat of the inactive tab,
 * and start listening to the active tab
 */
function stopHeartbeatPeacefully({
  sessionId,
  timerRef,
}: {
  timerRef: { current: number | null };
  sessionId: { current: number | null };
}) {
  // delete the page & associated data from the service worker script
  navigator?.serviceWorker?.controller?.postMessage({
    type: "unload",
    id: sessionId.current,
  });

  // stop sending the heartbeat
  if (timerRef.current) {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }
}
